import type { PlatformMessagingNotice, PlatformMessagingNoticeReceipt } from '@exylon/generated-contract-kernel/types';

import { boundedDispatch } from '../../runtime/bounded-dispatch';
import { createPolledRead, type PolledAnswer, type PolledRead } from '../../runtime/polled-read';
import { NOTICE_INBOX_OPERATION_NAMES as OPS, type NoticeInboxPage, type NoticeInboxPort, type NoticeInboxReadResult, type NoticeInboxScope, type NoticeReceiptAction } from './contract-port';
import { noticeDismissible } from './notice-model';
import { applyNoticeReceipt, NOTICE_INBOX_MAX_PAGES, NOTICE_INBOX_PAGE_SIZE, NOTICE_INBOX_SUMMARY_LIMIT, noticeInboxCovers, readNoticeInbox } from './read-model';

/** How many team notices the staff corner reads each time: only those still shown, so no newer history pushes one out. */
const STAFF_NOTICE_READ_SIZE = 50;
/** A background refresh (a poll tick, a tab coming back) never lands this close to another inbox request. */
const BACKGROUND_REFRESH_GAP_MS = 2_000;

type InboxFailure = 'transport' | 'authorization_denied' | 'invalid_response' | 'receipt_not_observed';

class InboxReadFailure extends Error {
  constructor(readonly reason: InboxFailure) { super(`session_inbox_${reason}`); }
}

interface InboxWindow {
  readonly items: readonly PlatformMessagingNotice[];
  readonly complete: boolean;
}

interface InboxValue {
  readonly unreadCount: number;
  /** The open inbox's newest notices; null until it was first opened. A closed inbox never reads them. */
  readonly window: InboxWindow | null;
  /** The team notices still shown, while a staff screen's corner watches them. */
  readonly staffNotices: readonly PlatformMessagingNotice[];
}

interface ReceiptAttempt {
  readonly key: string;
  readonly noticeId: string;
  /** The notice's place in the inbox order, to know whether a later read reached it. */
  readonly noticeCreatedAt: string;
  readonly action: NoticeReceiptAction;
  phase: 'unknown' | 'confirmed';
  pending: boolean;
  receipt?: PlatformMessagingNoticeReceipt;
}

export interface SessionInboxSnapshot {
  readonly value: InboxValue | null;
  readonly failure: InboxFailure | null;
  /** A refused receipt: the list asks for a new read before anything else. */
  readonly staleAfterRefusal: boolean;
  readonly pending: readonly string[];
  readonly errors: Readonly<Record<string, string>>;
  readonly pages: number;
}

export interface SessionInboxController {
  subscribe(listener: () => void): () => void;
  getSnapshot(): SessionInboxSnapshot;
  /** Reads what is on screen now, joining a read on its way. */
  refresh(): void;
  /** A poll tick or a return to the tab: dropped right after another request, and in a hidden tab. */
  refreshInBackground(pollIntervalMs: number): void;
  /** The session or the mount ended: no answer lands and nothing more is sent. `resume` undoes it (StrictMode's second effect). */
  retire(): void;
  resume(): void;
  /** The inbox's dialog opened or closed: opening reads its latest notices, closing forgets the older ones. */
  inboxPresence(present: boolean): void;
  loadOlder(): void;
  /** Records one action on one notice or on every copy of a repeated one; one read afterwards observes them all. */
  record(noticeIds: readonly string[], action: NoticeReceiptAction): Promise<void>;
  /** A staff screen's corner is on screen: the tick reads the team notices and takes the total from that same answer. */
  watchStaffNotices(): () => void;
  /** The corner's close: the recipient's own receipt, and the bell's total follows at once. */
  dismissStaffNotice(noticeId: string): void;
}

const failureOf = (error: unknown): InboxFailure => error instanceof InboxReadFailure ? error.reason : 'transport';

const ready = (result: NoticeInboxReadResult<NoticeInboxPage>): NoticeInboxPage => {
  if (result.kind === 'ready') return result.value;
  throw new InboxReadFailure(result.reason);
};

const UNCONFIRMED = 'No se ha recibido confirmación. Reintenta la misma acción; se conserva su clave de idempotencia.';

/**
 * One inbox per session: one poll serves the bell and the staff corner, a background tick keeps what is on screen, and
 * every receipt goes through here, so a close anywhere moves the bell at once.
 */
export function createSessionInboxController(options: {
  readonly port: NoticeInboxPort;
  readonly scope: NoticeInboxScope;
  /** The session allows reading and writing now (authorised, online). */
  readonly enabled: () => boolean;
  readonly createIdempotencyKey: () => string;
}): SessionInboxController {
  const { port, scope, enabled, createIdempotencyKey } = options;
  const listeners = new Set<() => void>();
  const attempts = new Map<string, ReceiptAttempt>();
  let open = 0;
  let pages = 1;
  let staffWatchers = 0;
  let readWindowOnce = false;
  let retired = false;
  /** What the read on its way asks for: a new reason to read joins it when it asks for the same. */
  let readOnTheWay: string | null = null;
  let issuedAt = Number.NEGATIVE_INFINITY;
  let local: Pick<SessionInboxSnapshot, 'staleAfterRefusal' | 'pending' | 'errors'> = { staleAfterRefusal: false, pending: [], errors: {} };
  let polled: PolledRead<InboxValue>;

  const compute = (): SessionInboxSnapshot => {
    const read = polled.getSnapshot();
    return { value: read.value, failure: read.failure === null ? null : failureOf(read.failure), ...local, pages };
  };
  let snapshot: SessionInboxSnapshot;
  const publish = () => {
    snapshot = compute();
    for (const listener of [...listeners]) listener();
  };
  const patch = (update: Partial<typeof local>) => { local = { ...local, ...update }; publish(); };
  const withoutErrors = (ids: readonly (string | undefined)[]) =>
    Object.fromEntries(Object.entries(local.errors).filter(([id]) => !ids.includes(id)));

  /* The tick's one read. Closed on a staff screen, the corner's team notices carry the total; closed elsewhere, one
     notice's read does; open, the window does (a staff screen with the inbox open keeps its corner current too). */
  const askedFor = () => JSON.stringify([readWindowOnce || open > 0, pages, staffWatchers > 0]);
  const read = async (entityTag: string | null): Promise<PolledAnswer<InboxValue>> => {
    const withWindow = readWindowOnce || open > 0;
    const asked = askedFor();
    readOnTheWay = asked;
    issuedAt = Date.now();
    const watchingStaff = staffWatchers > 0;
    // Asked with what is on screen, an unchanged inbox answers 304 and the window on screen stays.
    const page = await readNoticeInbox(port, scope, withWindow
      ? { notices: pages * NOTICE_INBOX_PAGE_SIZE, withStaffNotices: watchingStaff, entityTag }
      : watchingStaff ? { notices: STAFF_NOTICE_READ_SIZE, staffNoticesOnly: true, entityTag }
      : { notices: NOTICE_INBOX_SUMMARY_LIMIT, entityTag });
    if (readOnTheWay === asked) readOnTheWay = null;
    if (retired) throw new InboxReadFailure('transport');
    if (page.kind === 'not_modified') return page;
    const read = ready(page);
    const previous = polled.getSnapshot().value;
    return { kind: 'current', entityTag: read.revision ?? null, value: {
      unreadCount: read.unreadCount,
      window: withWindow ? { items: read.items, complete: read.complete } : previous?.window ?? null,
      staffNotices: read.staffNotices ?? (watchingStaff ? read.items : previous?.staffNotices ?? []),
    } };
  };

  /* A confirmed receipt clears once a read that reached its notice's place shows the receipt, or shows the notice gone.
     A read that did not reach it (newer notices pushed it past the notices read) neither clears nor fails it. */
  const observeReceipts = (value: InboxValue) => {
    const window = value.window;
    if (window === null || window === polled.getSnapshot().value?.window) return;
    const observed: string[] = [];
    for (const [attemptKey, attempt] of attempts) {
      if (attempt.phase !== 'confirmed' || !attempt.receipt) continue;
      const notice = window.items.find(({ id }) => id === attempt.noticeId);
      if (!notice) {
        if (noticeInboxCovers(window, { createdAt: attempt.noticeCreatedAt, id: attempt.noticeId })) observed.push(attemptKey);
        continue;
      }
      if (notice.version < attempt.receipt.version
        || (['seenAt', 'readAt', 'dismissedAt'] as const).some((field) => attempt.receipt?.[field] && notice[field] !== attempt.receipt[field])) {
        throw new InboxReadFailure('receipt_not_observed');
      }
      observed.push(attemptKey);
    }
    const observedIds = observed.map((attemptKey) => attempts.get(attemptKey)?.noticeId);
    for (const attemptKey of observed) attempts.delete(attemptKey);
    local = { ...local, staleAfterRefusal: false, errors: withoutErrors(observedIds) };
  };

  polled = createPolledRead<InboxValue>({ read, accept: observeReceipts });
  snapshot = compute();
  polled.subscribe(publish);

  /** What is asked for changed: a read that already asks for it is joined, any other is dropped. */
  const readAsked = (): void => {
    if (readOnTheWay === askedFor()) void polled.refresh();
    else void polled.invalidate();
  };
  const readWindowNow = (): Promise<void> => {
    readWindowOnce = true;
    const landed = polled.invalidate();
    readWindowOnce = false;
    return landed;
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    getSnapshot: () => snapshot,
    retire() { retired = true; },
    resume() { retired = false; },
    refresh() {
      if (enabled()) void polled.refresh();
    },
    refreshInBackground(pollIntervalMs) {
      if (!enabled() || document.visibilityState === 'hidden') return;
      if (Date.now() - issuedAt < Math.min(BACKGROUND_REFRESH_GAP_MS, pollIntervalMs / 2)) return;
      void polled.refresh();
    },
    inboxPresence(present) {
      open = Math.max(0, open + (present ? 1 : -1));
      if (present && open === 1 && enabled()) readAsked();
      if (!present && open === 0) { pages = 1; publish(); }
    },
    loadOlder() {
      if (open === 0 || pages >= NOTICE_INBOX_MAX_PAGES || !enabled()) return;
      pages += 1;
      publish();
      readAsked();
    },
    async record(noticeIds, action) {
      const window = polled.getSnapshot().value?.window;
      if (!enabled() || !window || polled.getSnapshot().failure !== null || local.staleAfterRefusal) return;
      const started: { readonly noticeId: string; readonly attemptId: string; readonly attempt: ReceiptAttempt; readonly notice: PlatformMessagingNotice }[] = [];
      for (const noticeId of new Set(noticeIds)) {
        const notice = window.items.find((item) => item.id === noticeId);
        if (!notice || (action === 'dismiss' && !noticeDismissible(notice))
          || [...attempts.values()].some((attempt) => attempt.noticeId === noticeId && attempt.pending)) continue;
        const attemptId = `${noticeId}:${action}`;
        let attempt = attempts.get(attemptId);
        if (!attempt) {
          const key = createIdempotencyKey();
          if (!/^[\x21-\x7e]{1,200}$/.test(key)) continue;
          attempt = { key, noticeId, noticeCreatedAt: notice.createdAt, action, phase: 'unknown', pending: false };
          attempts.set(attemptId, attempt);
        }
        attempt.pending = true;
        started.push({ noticeId, attemptId, attempt, notice });
      }
      if (!started.length) return;
      const ids = started.map(({ noticeId }) => noticeId);
      patch({ pending: [...local.pending.filter((id) => !ids.includes(id)), ...ids], errors: withoutErrors(ids) });
      const confirmed: typeof started = [];
      try {
        // At most four receipts in flight. Each copy sends the key its attempt retained, so a copy whose answer was lost
        // retries with the same key. One read afterwards observes every confirmed copy.
        await boundedDispatch(started, async (entry) => {
          const { noticeId, attemptId, attempt, notice } = entry;
          try {
            if (attempt.phase !== 'confirmed') {
              const common = { params: { noticeId }, query: { restaurantId: scope.restaurantId }, body: {}, idempotencyKey: attempt.key };
              const result = action === 'seen' ? await port.seen({ ...common, operation: OPS.seen })
                : action === 'read' ? await port.read({ ...common, operation: OPS.read })
                  : await port.dismiss({ ...common, operation: OPS.dismiss });
              if (result.kind !== 'success') {
                attempts.delete(attemptId);
                patch({ staleAfterRefusal: true,
                  errors: { ...local.errors, [noticeId]: 'No se ha confirmado el cambio de tu copia. Vuelve a leer el buzón antes de reintentar.' } });
                return;
              }
              if (!applyNoticeReceipt(notice, result.canonical, action)) throw new Error('notice_receipt_invalid');
              attempt.phase = 'confirmed';
              attempt.receipt = result.canonical;
            }
            confirmed.push(entry);
          } catch {
            patch({ errors: { ...local.errors, [noticeId]: UNCONFIRMED } });
          }
        }, { retired: () => retired });
        if (!confirmed.length || retired) return;
        // The read after a receipt always goes to the server and reaches the notice's place.
        await readWindowNow();
        const unobserved = confirmed.filter(({ attemptId, attempt }) => attempts.get(attemptId) === attempt).map(({ noticeId }) => noticeId);
        if (unobserved.length) {
          patch({ errors: { ...local.errors, ...Object.fromEntries(unobserved.map((id) =>
            [id, 'El acuse se ha confirmado. Reintentar vuelve a leer el buzón; no repite el acuse.'])) } });
        }
      } finally {
        for (const { attempt } of started) attempt.pending = false;
        patch({ pending: local.pending.filter((id) => !ids.includes(id)) });
      }
    },
    watchStaffNotices() {
      staffWatchers += 1;
      if (staffWatchers === 1 && enabled()) readAsked();
      let watching = true;
      return () => {
        if (!watching) return;
        watching = false;
        staffWatchers -= 1;
      };
    },
    dismissStaffNotice(noticeId) {
      const notice = polled.getSnapshot().value?.staffNotices.find(({ id }) => id === noticeId);
      if (!enabled() || notice === undefined || !noticeDismissible(notice)) return;
      void port.dismiss({ operation: OPS.dismiss, params: { noticeId }, query: { restaurantId: scope.restaurantId }, body: {}, idempotencyKey: createIdempotencyKey() })
        .then((result) => { if (result.kind === 'success') void polled.invalidate(); })
        .catch(() => undefined);
    },
  };
}
